import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../users/AuthContext';
import { createAPIUtilities } from '../api/authAPI';

/**
 * Custom hook for managing groups
 * Provides state management and CRUD operations for groups
 */
export const useGroups = () => {
  const { makeAuthenticatedRequest } = useAuth();
  const { groupAPI } = createAPIUtilities(makeAuthenticatedRequest);
  
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all groups
  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await groupAPI.listGroups();
      
      if (result.success) {
        setGroups(result.data);
      } else {
        setError(result.error);
        setGroups([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch groups');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new group
  const createGroup = useCallback(async (groupName) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await groupAPI.createGroup(groupName);
      
      if (result.success) {
        // Refresh groups list
        await fetchGroups();
        return { success: true, message: result.message };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const error = err.message || 'Failed to create group';
      setError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  }, [fetchGroups]);

  // Delete a group
  const deleteGroup = useCallback(async (groupName) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await groupAPI.deleteGroup(groupName);
      
      if (result.success) {
        // Refresh groups list
        await fetchGroups();
        return { success: true, message: result.message };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const error = err.message || 'Failed to delete group';
      setError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  }, [fetchGroups]);

  // Get group details
  const getGroupDetails = useCallback(async (groupName) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await groupAPI.getGroupDetails(groupName);
      return result;
    } catch (err) {
      const error = err.message || 'Failed to get group details';
      setError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  }, []);

  // Find group by name
  const findGroupByName = useCallback(async (groupName) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await groupAPI.findGroupByName(groupName);
      return result;
    } catch (err) {
      const error = err.message || 'Failed to search group';
      setError(error);
      return { success: false, error, data: null };
    } finally {
      setLoading(false);
    }
  }, []);

  // Add user to group
  const addUserToGroup = useCallback(async (groupName, userUID) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await groupAPI.addUserToGroup(groupName, userUID);
      
      if (result.success) {
        // Refresh groups list to show updated membership
        await fetchGroups();
        return { success: true, message: result.message };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const error = err.message || 'Failed to add user to group';
      setError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  }, [fetchGroups]);

  // Remove user from group
  const removeUserFromGroup = useCallback(async (groupName, userUID) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await groupAPI.removeUserFromGroup(groupName, userUID);
      
      if (result.success) {
        // Refresh groups list to show updated membership
        await fetchGroups();
        return { success: true, message: result.message };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const error = err.message || 'Failed to remove user from group';
      setError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  }, [fetchGroups]);

  // Add subgroup to group (nest group)
  const addSubgroupToGroup = useCallback(async (parentGroupName, subGroupName) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await groupAPI.addSubgroupToGroup(parentGroupName, subGroupName);
      
      if (result.success) {
        // Refresh groups list to show updated hierarchy
        await fetchGroups();
        return { success: true, message: result.message };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const error = err.message || 'Failed to add subgroup';
      setError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  }, [fetchGroups]);

  // Filter groups locally
  const filterGroups = useCallback((searchTerm) => {
    if (!searchTerm) return groups;
    
    return groups.filter(group => {
      const name = group.name || '';
      const description = group.description || '';
      
      const searchLower = searchTerm.toLowerCase();
      return name.toLowerCase().includes(searchLower) ||
             description.toLowerCase().includes(searchLower);
    });
  }, [groups]);

  // Get group statistics
  const getGroupStats = useCallback(() => {
    const totalGroups = groups.length;
    const groupsWithMembers = groups.filter(g => g.members && g.members.length > 0).length;
    const totalMembers = groups.reduce((total, group) => {
      return total + (group.members ? group.members.length : 0);
    }, 0);
    
    return {
      totalGroups,
      groupsWithMembers,
      emptyGroups: totalGroups - groupsWithMembers,
      totalMembers,
      averageMembersPerGroup: totalGroups > 0 ? (totalMembers / totalGroups).toFixed(1) : 0
    };
  }, [groups]);

  // Initial load
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  return {
    groups,
    loading,
    error,
    fetchGroups,
    createGroup,
    deleteGroup,
    getGroupDetails,
    findGroupByName,
    addUserToGroup,
    removeUserFromGroup,
    addSubgroupToGroup,
    filterGroups,
    getGroupStats,
    refresh: fetchGroups
  };
};
